import type { Territory } from "$global-providers/config-provider/provider";
import { TERRITORY } from "$routes/_lib/constants";
import { Role } from "elephant-web/gql";

const OPEN_BRACKET = "%7B";
const CLOSE_BRACKET = "%7D";

function combineDataSources(
  dataSources: string[],
  paramValue: string,
  paramName = "clinic",
) {
  const dataSourcesCombined = dataSources.map((source) => {
    return `"${source}":${OPEN_BRACKET}"${paramName}":"${paramValue}"${CLOSE_BRACKET}`;
  });
  return `${OPEN_BRACKET}${dataSourcesCombined.join(",")}${CLOSE_BRACKET}`;
}

// ds0 - ds10
const DEFAULT_DATA_SOURCES = Array(11)
  .fill(null)
  .map((_, i) => `ds${i}`);

const makeDataStudioLink = ({
  dashboardId,
  pageId,
  dataSources = DEFAULT_DATA_SOURCES,
  dataSourceParamValue,
  dataSourceParamName = "clinic",
}) => {
  return `https://datastudio.google.com/u/0/reporting/${dashboardId}/page/${pageId}?params=${combineDataSources(
    dataSources,
    dataSourceParamValue,
    dataSourceParamName,
  )}`;
};

const makeLookerStudioLink = ({
  dashboardId,
  pageId,
  dataSources = DEFAULT_DATA_SOURCES,
  dataSourceParamValue,
  dataSourceParamName = "clinic",
}) => {
  return `https://lookerstudio.google.com/u/0/reporting/${dashboardId}/page/${pageId}?params=${combineDataSources(
    dataSources,
    dataSourceParamValue,
    dataSourceParamName,
  )}`;
};

const FACILITY_DATA_SOURCES = [
  "ds111",
  "ds120",
  "ds117",
  "ds112",
  "ds121",
  "ds118",
  "ds135",
  "ds137",
  "ds141",
  "ds103",
  "ds133",
  ...DEFAULT_DATA_SOURCES,
];

export const ALLOWED_FACILITY_ROLES: string[] = [
  Role.Doctor,
  Role.Clinic,
  Role.Elephant,
];
export const ALLOWED_LABORATORY_ROLES: string[] = [Role.Technician];
export const ALLOWED_BILLING_ROLES: string[] = [Role.Clinic];

export const ALLOWED_FACILITY_INVENTORY_ROLES: string[] = [
  Role.Admin,
  Role.Clinic,
  Role.Pharmacist,
  Role.Elephant,
];
export const ALLOWED_ORGANISATION_INVENTORY_ROLES: string[] = [
  Role.Admin,
  Role.Elephant,
];

const LAB_DATA_SOURCES = ["ds27", "ds100", "ds101", ...DEFAULT_DATA_SOURCES];
interface LinkParams {
  roleName: string;
  territory: Territory;
  facilityId: string;
  organisationId: string;
}

const linkGenerators = {
  FACILITY: ({ facilityId, roleName, territory }: LinkParams) => {
    if (!ALLOWED_FACILITY_ROLES.includes(roleName)) {
      return null;
    }

    if (territory === TERRITORY.PAKISTAN) {
      return makeDataStudioLink({
        dashboardId: "b334f460-eeec-45ef-84d3-2ac1d403ff8c",
        pageId: "rVL6",
        dataSourceParamValue: facilityId,
        dataSources: FACILITY_DATA_SOURCES,
      });
    }

    return makeDataStudioLink({
      dashboardId: "c132381a-411a-4a28-8f09-c026669e8041",
      pageId: "rVL6",
      dataSourceParamValue: facilityId,
      dataSources: FACILITY_DATA_SOURCES,
    });
  },
  LABORATORY: ({ facilityId, roleName, territory }: LinkParams) => {
    if (!ALLOWED_LABORATORY_ROLES.includes(roleName)) {
      return null;
    }

    if (territory === TERRITORY.PAKISTAN) {
      return makeDataStudioLink({
        dashboardId: "567acf70-14b5-4275-b9db-bdd5d7f0f7a3",
        pageId: "7D27",
        dataSourceParamValue: facilityId,
        dataSources: LAB_DATA_SOURCES,
      });
    }

    return makeDataStudioLink({
      dashboardId: "c8e3ed2c-c8e4-4375-afa2-507b22f4ed3e",
      pageId: "7D27",
      dataSourceParamValue: facilityId,
      dataSources: LAB_DATA_SOURCES,
    });
  },
  FACILITY_INVENTORY: ({ facilityId, roleName }: LinkParams) =>
    ALLOWED_FACILITY_INVENTORY_ROLES.includes(roleName) &&
    makeDataStudioLink({
      dashboardId: "24333a12-544b-4d2e-89b5-80268ebe5625",
      pageId: "7D27",
      dataSourceParamValue: facilityId,
    }),
  ORGANISATION_INVENTORY: ({ organisationId, roleName }: LinkParams) =>
    ALLOWED_ORGANISATION_INVENTORY_ROLES.includes(roleName) &&
    makeDataStudioLink({
      dashboardId: "b7a8400e-7654-4b3a-a9ca-ceec79d7fb28",
      pageId: "7D27",
      dataSourceParamValue: organisationId,
      dataSourceParamName: "organisation",
    }),
  BILLING: ({ facilityId, roleName, territory }: LinkParams) => {
    if (!ALLOWED_BILLING_ROLES.includes(roleName)) {
      return null;
    }

    if (territory === TERRITORY.PAKISTAN) {
      return null;
    }

    return makeLookerStudioLink({
      dashboardId: "d0392fe5-77bf-4ab8-8ea6-6e6c503b8665",
      pageId: "p_0r51ha3hsc",
      dataSourceParamValue: facilityId,
      dataSources: ["ds1", "ds2", "ds3", "ds4", "ds5", "ds6"],
    });
  },
  MONTHLY_SUMMARY: ({ facilityId, roleName, territory }: LinkParams) => {
    if (territory !== TERRITORY.NIGERIA) {
      return null;
    }
    return makeLookerStudioLink({
      dashboardId: "50d53dcf-6df5-4e13-b662-a59c98bd5436",
      pageId: "p_d8mk2e03ld",
      dataSourceParamValue: facilityId,
      dataSourceParamName: "clinic",
      dataSources: [
        "ds1",
        "ds2",
        "ds3",
        "ds4",
        "ds5",
        "ds6",
        "ds7",
        "ds8",
        "ds9",
        "ds10",
        "ds11",
        "ds12",
        "ds111",
      ],
    });
  },
};

export type DASHBOARD_TYPE = keyof typeof linkGenerators;

interface GenerateDashboardURLProps {
  facilityId: string;
  organisationId: string;
  territory: Territory;
  type: DASHBOARD_TYPE;
  roleName?: string;
}

export const generateDashboardURL = ({
  facilityId,
  organisationId,
  territory,
  type,
  roleName,
}: GenerateDashboardURLProps): string => {
  if (territory === TERRITORY.LIBERIA) {
    return null;
  }

  const link = linkGenerators[type]({
    territory,
    facilityId,
    organisationId,
    roleName,
  });

  return link || null;
};
